/**
 * useActiveWorkspace — switch the caller's active workspace.
 *
 * S-077 / FEAT-006. The PublicationsView + TeamView "workspace
 * selector" originally just changed what list the SPA *displayed*.
 * That worked for reads (RLS allows cross-workspace SELECT when the
 * user has membership) but every mutation 500'd with an RLS policy
 * violation because the JWT's agencyclient_id claim still pointed at
 * the onboarding-default workspace.
 *
 * This hook actually flips the active workspace:
 *
 *   1. PUT /users/me/active-agencyclient { agencyclient_id }
 *      → server writes Clerk user publicMetadata.active_agencyclient_id
 *        + records an audit row with action "membership.activate".
 *      → 403 if the user has no membership on the target workspace.
 *
 *   2. Force-refresh the JWT — getToken({ template, skipCache: true })
 *      → next mutation carries the new agencyclient_id claim.
 *
 *   3. Invalidate every broad query key that is scoped (directly or
 *      indirectly) to the active agencyclient: agencyclients itself,
 *      publications, voice-profiles, brand-identities, memberships,
 *      invitations. The selector's parent view will refetch with the
 *      new active workspace.
 *
 * The hook returns:
 *   - activate(agencyclientId) — async; resolves after the JWT refresh
 *     completes AND the new token has been accepted by the server on
 *     a sanity-check GET so the caller can update local state safely.
 *   - isActivating — true while step 1, step 2, or step 3 is in flight.
 *   - error — ApiError or null. Set when step 1 fails (403 most
 *     commonly). The caller is expected to surface the error inline
 *     next to the selector and revert the selection.
 *
 * Activation is a no-op if `agencyclientId` equals the JWT's current
 * `agencyclient_id` — the hook still refreshes the JWT and resolves
 * but skips the API roundtrip. This keeps the selector's onChange
 * handler simple (it can call activate unconditionally).
 *
 * Note for browser smoke: after this lands, the user must hard-refresh
 * the SPA once to pick up the new code; otherwise the old selector
 * code path (no activate call) is still served from the bundle cache.
 *
 * Flicker fix (Phase 5 UX polish, 2026-05-25): the selector now waits
 * for `activate()` to resolve before updating its local state. That,
 * combined with the sanity-check round-trip below, eliminates the
 * empty-state flash that used to appear between "switched to B" and
 * "publications-for-B loaded". The flash was caused by the publications
 * query refetching with key=[publications, B_id] under the OLD JWT
 * (returning zero rows under RLS) before the JWT refresh propagated.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi } from "./api";
import { isPieceScopedQueryKey } from "./workspace-selection";

// Query keys that are scoped (directly or via parent agencyclient
// joins) to the active workspace. Invalidated on every successful
// switch so the SPA refetches against the new claim. New scoped
// resources added in later phases should append themselves here.
const TENANT_SCOPED_KEYS: ReadonlyArray<readonly [string]> = [
  ["agencyclients"],
  ["publications"],
  ["voice-profiles"],
  ["brand-identities"],
  ["memberships"],
  ["invitations"],
  ["amplifiers"],
  ["amplification-rules"],
  ["publish-targets"],
  ["api-keys"],
  ["pieces"],
  ["social-accounts"],
  ["newsletters"],
];

// Must match apps/web/src/lib/api.ts JWT_TEMPLATE. Kept as a local
// constant to avoid an export-only-for-this surface from api.ts.
const JWT_TEMPLATE = "ccm-default";

export interface UseActiveWorkspaceResult {
  activate: (agencyclientId: number) => Promise<void>;
  isActivating: boolean;
  error: ApiError | null;
  clearError: () => void;
}

interface ActivateResponse {
  active_agencyclient_id: number;
}

export function useActiveWorkspace(): UseActiveWorkspaceResult {
  const api = useApi();
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const activate = useCallback(
    async (agencyclientId: number) => {
      setError(null);
      setIsActivating(true);
      try {
        await api<ActivateResponse>("/users/me/active-agencyclient", {
          method: "PUT",
          body: JSON.stringify({ agencyclient_id: agencyclientId }),
        });

        // Force the next JWT mint to carry the new active_agencyclient_id.
        // skipCache: true bypasses Clerk's in-memory token cache so the
        // first mutation post-switch already lands under the new claim.
        await getToken({ template: JWT_TEMPLATE, skipCache: true });

        // Sanity round-trip — Clerk's getToken resolves when the new
        // token is MINTED, not when it's been accepted by our API. On
        // a slow network the next refetch could still race the JWT
        // propagation and return zero-row results under RLS for the
        // OLD agencyclient scope. One throwaway GET against an endpoint
        // that does not depend on the scoped value (here: the global
        // agencyclients list for the tenant) forces the new token
        // through one full round-trip before we resolve, so the
        // subsequent invalidations + caller-triggered queries are
        // guaranteed to land under the new claim.
        await api<unknown>("/agencyclients").catch(() => {
          // Non-fatal — if this fails we still proceed with invalidation.
          // The caller's queries will retry with their own error handling.
        });

        // Broad invalidation — every tenant-scoped query refetches with
        // the new active workspace in scope. We use invalidateQueries
        // (not refetchQueries) so inactive queries are marked stale and
        // refetch only when their views remount or refocus, keeping the
        // network burst minimal on the active page.
        await Promise.all([
          ...TENANT_SCOPED_KEYS.map((key) =>
            qc.invalidateQueries({ queryKey: key })
          ),
          // BUG-137: an open piece's own rows are scoped the same way.
          qc.invalidateQueries({
            predicate: (q) => isPieceScopedQueryKey(q.queryKey),
          }),
        ]);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(
            new ApiError(0, String(err), "Could not switch workspace")
          );
        }
        // Re-throw so callers can revert local state in their .catch
        // handler. The hook still surfaces error via the returned
        // state for inline display.
        throw err;
      } finally {
        setIsActivating(false);
      }
    },
    [api, getToken, qc]
  );

  const clearError = useCallback(() => setError(null), []);

  return { activate, isActivating, error, clearError };
}

// Decode the `agencyclient_id` claim from a Clerk JWT without a library.
// JWTs are base64url-encoded `header.payload.signature`. We only read the
// payload — signature verification is the server's job. Returns null on any
// parse failure so the caller treats it as "claim unknown" and re-syncs.
function readAgencyclientClaim(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    ) as { agencyclient_id?: unknown };
    return json.agencyclient_id == null ? null : Number(json.agencyclient_id);
  } catch {
    return null;
  }
}

/**
 * useEnsureActiveWorkspace — sync the Clerk JWT's `agencyclient_id` claim to
 * the workspace the current view is displaying.
 *
 * S-199 / FEAT-006. Each list view (Kanban, Calendar, Publish targets, …)
 * defaults its displayed workspace to `workspaces[0]` or a sessionStorage
 * value, independent of the JWT. The two-level RLS policies scope reads AND
 * writes by `app.agencyclient_id`, which is set from the JWT's
 * `agencyclient_id` claim (apps/api/src/middleware/rlsContext.ts). When the
 * displayed workspace ≠ the claim, the view shows an empty list and every
 * mutation (e.g. create piece) fails — until the user switches workspace and
 * back, which calls `activate()` and finally syncs the claim.
 *
 * This hook closes that gap: on mount (and whenever `desiredId` changes) it
 * decodes the current token's claim and calls `activate(desiredId)` only when
 * they differ. The decode-and-compare avoids a redundant activate (with its
 * broad query invalidation) on every navigation and right after an explicit
 * switch — at that point the claim already matches.
 */
export function useEnsureActiveWorkspace(
  desiredId: number | null | undefined
): void {
  const { getToken } = useAuth();
  const { activate } = useActiveWorkspace();
  const syncedRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const wantedRef = useRef<number | null | undefined>(desiredId);

  useEffect(() => {
    wantedRef.current = desiredId;
    if (desiredId == null) return;
    if (syncedRef.current === desiredId) return;
    // BUG-137: a sync already running picks up the new target when it
    // finishes, rather than leaving the claim on the old one.
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    void (async () => {
      try {
        let target = wantedRef.current;
        while (target != null && syncedRef.current !== target) {
          const token = await getToken({ template: JWT_TEMPLATE });
          const claim = token ? readAgencyclientClaim(token) : null;
          if (claim !== target) {
            await activate(target);
          }
          syncedRef.current = target;
          target = wantedRef.current;
        }
      } catch {
        // Non-fatal — an explicit switch or the next mount retries the sync.
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [desiredId, getToken, activate]);
}
